import type { HistorySessionDetail } from '../../../services/historyTypes'
import { channelApi, recordingApi } from '../../../services/api'
import { SectionCard } from '../../patterns/SectionCard'
import { KpiCard } from '../../patterns/KpiCard'
import { EmptyState } from '../../patterns/EmptyState'
import { Users, Mic, MessageSquare, Video, Pin, Activity } from 'lucide-react'
import ActivityFeed from '../ActivityFeed'

interface TagDef { id: string; label: string; color: string | null }
export default function SessionDetail({ detail, filters, onFilters, isHost, vocab, channelId, onChanged }: {
  detail: HistorySessionDetail
  filters: { tagId?: string; pinned?: boolean; q?: string }
  onFilters: (f: { tagId?: string; pinned?: boolean; q?: string }) => void
  isHost: boolean
  vocab: TagDef[]
  channelId: string
  onChanged: () => void
}) {
  const { session, participants, transcript, recordings, activity } = detail
  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Duration" value={`${Math.floor(session.durationSec / 60)}m`} icon={<Mic size={16} />} />
        <KpiCard label="Participants" value={participants.length} icon={<Users size={16} />} />
        <KpiCard label="Topic" value={session.aiTopic ?? 'Pending'} hint={session.aiTopic ? undefined : 'AI will fill this in'} />
      </div>

      <SectionCard icon={<Users size={16} />} title="Participants">
        <div className="flex flex-wrap gap-2">
          {participants.map((p) => (
            <span key={(p.userId ?? p.guestId) as string} className="px-2 py-1 rounded-md bg-muted text-sm text-foreground">
              {p.displayName}{p.isGuest && <span className="text-xs text-muted-foreground"> - guest</span>}
            </span>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={<Mic size={16} />} title="Transcript">
        {transcript.status === 'processing'
          ? <EmptyState size="sm" title="Transcript is processing" description="The canonical transcript will appear once ready." />
          : <div className="space-y-1 text-sm">
              {transcript.utterances.map((u, i) => (
                <div key={i}><span className="text-muted-foreground">{u.speakerName}: </span><span className="text-foreground">{u.text}</span></div>
              ))}
            </div>}
      </SectionCard>

      <SectionCard icon={<Activity size={16} />} title="Activity">
        <ActivityFeed events={activity} />
      </SectionCard>

      <SectionCard icon={<MessageSquare size={16} />} title="Chat">
        <div className="flex items-center gap-2 mb-3">
          <select value={filters.tagId ?? ''} onChange={(e) => onFilters({ ...filters, tagId: e.target.value || undefined })}
            className="px-2 py-1 rounded-md bg-muted text-sm text-foreground">
            <option value="">All tags</option>
            {vocab.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-sm text-muted-foreground">
            <input type="checkbox" checked={!!filters.pinned} onChange={(e) => onFilters({ ...filters, pinned: e.target.checked || undefined })} /> Pinned
          </label>
          <input value={filters.q ?? ''} onChange={(e) => onFilters({ ...filters, q: e.target.value || undefined })}
            placeholder="Search chat" className="flex-1 px-2 py-1 rounded-md bg-muted text-sm text-foreground" />
        </div>
        {detail.messages.length === 0
          ? <EmptyState size="sm" title="No messages" />
          : <div className="space-y-2 text-sm">
              {detail.messages.map((m) => (
                <div key={m.id} className="group flex items-start gap-2">
                  {m.isPinned && <Pin size={12} className="mt-1 text-primary shrink-0" />}
                  <div className="min-w-0">
                    <span className="text-muted-foreground">{m.authorName ?? 'User'}: </span>
                    <span className="text-foreground">{m.content}</span>
                    {m.tags.length > 0 && (
                      <span className="ml-2 inline-flex gap-1 align-middle">
                        {m.tags.map((t) => (
                          <span key={t.id} className="px-1.5 py-0.5 rounded text-[11px] inline-flex items-center gap-1 bg-muted text-foreground"
                            style={{ background: t.color ? t.color + '33' : undefined, color: t.color ?? undefined }}>
                            {t.label}
                            {isHost && <button onClick={() => channelApi.untagMessage(channelId, m.id, t.id).then(onChanged)} className="opacity-60 hover:opacity-100">x</button>}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  {isHost && (
                    <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 shrink-0">
                      <select defaultValue="" onChange={(e) => { if (e.target.value) { channelApi.tagMessage(channelId, m.id, e.target.value).then(onChanged); e.target.value = '' } }}
                        className="px-1 py-0.5 rounded bg-muted text-[11px] text-muted-foreground" title="Add tag">
                        <option value="">+ tag</option>
                        {vocab.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                      <button onClick={() => channelApi.pinMessage(channelId, m.id, !m.isPinned).then(onChanged)}
                        className={`p-1 rounded hover:bg-accent ${m.isPinned ? 'text-primary' : 'text-muted-foreground'}`} title={m.isPinned ? 'Unpin' : 'Pin'}>
                        <Pin size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>}
      </SectionCard>

      {recordings.length > 0 && (
        <SectionCard icon={<Video size={16} />} title="Recordings">
          {recordings.map((r) => (
            <a key={r.id} href={r.fileUrl ? recordingApi.streamUrl(r.id) : '#'} target="_blank" rel="noreferrer" className="block text-sm text-primary hover:underline">
              {new Date(r.startedAt).toLocaleString()} - {Math.floor(r.duration / 60)}m
            </a>
          ))}
        </SectionCard>
      )}
    </div>
  )
}
